import { ApiError } from '../../api/client.ts';
import { GuestApiError } from '../../api/guest.ts';
import type { InlineAlertVariant } from '../../ui/index.ts';
import type { MessageKey } from '../../i18n/messages.en.ts';
import { describeQueuePosition } from './guestRefetch.ts';

/**
 * SPEC §6.7's guest-facing copy for every code the request flow can actually produce, in one
 * place — the same "one testable home instead of inlining this at every call site" pattern
 * `strapi-template/src/utils/request-guard.js` already uses server-side.
 *
 * MVP-272 Phase 1: this module returns i18n KEYS (+ params), never rendered English — a pure
 * module (no React, no DOM, unit-tested under `node --test`/vitest with no JSX transform) has no
 * business importing the dictionary itself just to build a sentence; the one caller with a real
 * `useT()` (`RequestPage.tsx`/`RequestSheet.tsx`/`RateLimitNotice.tsx`) resolves `t(key, params)`.
 * `detail` in particular is USUALLY the server's own `message` string (every message
 * request-guard.js/song-request's controller send already follows DESIGN.md §7's "what happened,
 * which thing, what to do" rule, so authoring a parallel client-side copy of the same sentence
 * would only risk the two drifting apart) — `'guest.errors.serverMessage'` (`messages.en.ts`:
 * `'{message}'`) is the identity passthrough key for exactly that case, so every branch below still
 * resolves the same uniform way (`t(copy.detailKey, copy.detailParams)`) whether the text is
 * app-authored or server-authored/dynamic.
 */

export interface RequestErrorCopy {
  titleKey: MessageKey;
  titleParams?: Record<string, string | number>;
  detailKey: MessageKey;
  detailParams?: Record<string, string | number>;
  /** Present only when the server sent one (429 quota/rate-limit responses, and `DEVICE_BLOCKED`'s
   * remaining block time) — `RateLimitNotice` renders a live countdown from it. */
  retryAfterSec?: number;
  tone: InlineAlertVariant;
}

const TITLE_KEY_BY_CODE: Record<string, MessageKey> = {
  CODE_REQUIRED: 'guest.errors.codeRequired',
  // P2 fix (round 5): `detail` below is always the server's own message, which for this code is
  // literally "This link isn't live anymore — ask the KJ for a fresh QR code or link." (session's
  // `evaluateSessionAvailability`) — the OLD title here repeated that same phrase verbatim as the
  // heading, so a typo'd/never-existed code (a plain 404, nothing "expired" about it) read as the
  // same sentence twice. request-guard.js sends this one code for BOTH "never existed" and "was
  // live, now isn't" (a server-side conflation out of this wave's reach), so a neutral title covers
  // both without asserting either history.
  // P2 fix (round 6 critic): title only — see the `SESSION_NOT_FOUND_DETAIL_OVERRIDE` doc comment
  // just below for why `detail` for THIS code no longer comes from the server's own message.
  SESSION_NOT_FOUND: 'guest.errors.sessionNotFound',
  REQUESTS_CLOSED: 'guest.errors.requestsClosed',
  PASSCODE_INVALID: 'guest.errors.passcodeInvalid',
  SONG_REQUIRED: 'guest.errors.songRequired',
  DEVICE_ID_REQUIRED: 'guest.errors.deviceIdRequired',
  SINGER_NAME_REQUIRED: 'guest.errors.singerNameRequired',
  SINGER_NAME_INVALID: 'guest.errors.singerNameInvalid',
  DUPLICATE_REQUEST: 'guest.errors.duplicateRequest',
  DEVICE_BLOCKED: 'guest.errors.deviceBlocked',
  // The SAME code covers THREE server-side reasons (request-guard.js's `evaluateDeviceQuota`):
  // the per-device "pending" gate (`reason: 'pending'`, `maxPendingPerDevice`), the rolling
  // "window" quota (`reason: 'window'`), plus the coarse per-route `global::rate-limit`
  // middleware (5 requests / 5 min) — all three send `details.code: 'RATE_LIMITED'` on the wire.
  // This default only ever fires for 'window'/global-limit (see `describeRequestError`'s own
  // `retryAfterSec`-based override for 'pending', request-guard.js's only reason with no timer).
  RATE_LIMITED: 'guest.errors.rateLimited',
  REQUESTS_FULL: 'guest.errors.requestsFull',
  NETWORK_ERROR: 'guest.errors.networkError',
};

/**
 * MVP-473. The two codes `api/client.ts` raises ITSELF when `fetch` never reached a server —
 * `networkError()` builds their `message` as `"${code} ${host}"`, and says so above itself: "for a
 * stack trace, never for a screen." The branch below used to ask only whether `error.message` was
 * non-empty, so a guest whose signal dropped mid-scroll read `SERVER_UNREACHABLE
 * backend-mv.rmspublishing.biz` in a red box under the song list — seen live on production during
 * MVP-451's verification.
 *
 * `guest.errors.serverMessage` stays exactly what it was for every OTHER code: the passthrough for a
 * sentence the SERVER wrote in human language. These two have no server behind them to write one.
 * Same keys and the same `details.host` interpolation `api/errorMessage.ts` already uses for the
 * console's half of this (MVP-390) — the guest page simply never went through it.
 */
const NETWORK_DETAIL_KEY: Record<string, MessageKey> = {
  OFFLINE: 'server.offline',
  SERVER_UNREACHABLE: 'server.unreachable',
};

const TONE_BY_CODE: Record<string, InlineAlertVariant> = {
  SESSION_NOT_FOUND: 'info',
  REQUESTS_CLOSED: 'info',
  DUPLICATE_REQUEST: 'info',
  RATE_LIMITED: 'accent',
  REQUESTS_FULL: 'accent',
};

// P2 fix (round 6 critic): `SESSION_NOT_FOUND` is the one code in this file where `detail` does not
// come from the server's own message — request-guard.js's `evaluateSessionAvailability` sends the
// literal sentence "This link isn't live anymore — ask the KJ for a fresh QR code or link." for
// BOTH "this join code never existed" (a plain 404 on the very first `GET /api/sessions/join`, e.g.
// a mistyped or made-up code like `/j/ZZZZZZ`) AND "this session WAS live and has since
// rotated/closed" — a server-side conflation out of this wave's web/-only reach. `RequestPage.tsx`
// only ever routes an initial 404 through `describeRequestError()` (this is the COLD-LOAD path,
// `loadSession()`'s catch) — a session that was live in THIS tab and then rotates/closes is caught
// separately, live, off the `public` channel's `session.updated` frame, with its own
// `guest.gate.linkExpired*` copy that intentionally still asserts that history (unaffected by this
// override). A code that never existed never had a "link" to go dead, so the cold-load copy no
// longer claims one.
const SESSION_NOT_FOUND_DETAIL_KEY: MessageKey = 'guest.errors.sessionNotFoundDetail';

/**
 * R11-P2-2 fix (round 11 critic, reproduced live): `RequestPage.tsx`'s `handleSubmit` used to treat
 * ANY `GuestApiError` carrying a `.data` payload (only ever a 409 `DUPLICATE_REQUEST` — see
 * `api/guest.ts`'s `GuestApiError` doc comment) as a silent soft success: the sheet closed with no
 * message at all, on the assumption the pre-existing request it carried was good enough to show.
 * Live repro: re-requesting a song already in this device's queue closed the sheet with nothing
 * visibly different on screen. That is swallowing a real mistake, not a success — `handleSubmit`
 * now keeps the sheet open and shows `describeRequestError(err)`'s copy (title "Already on the
 * list", detail the server's own "You already asked for this song tonight.") like any other
 * rejected submit, and separately refreshes this device's own request card
 * (`RequestPage`'s `scheduleMineRefetch`) so the existing live request is reflected there without
 * a reload. Pulled out here (not inlined in the component) so the "is this THAT specific 409"
 * check is unit-testable without a DOM.
 */
export function isDuplicateRequestError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.code === 'DUPLICATE_REQUEST';
}

function retryAfterOf(error: ApiError): number | undefined {
  const raw = error.details?.retryAfterSec;
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
}

/**
 * Pure — turns whatever `api/guest.ts` threw into a key+params `RequestPage`/`RequestSheet`/
 * `RateLimitNotice` can resolve via `t()`, and never throws itself. A code this module has never
 * seen (a future server code, or a plain `Error`/string from somewhere else entirely) still gets
 * honest, non-generic copy — DESIGN.md §7 bans "Oops", not "we have no specific heading for this".
 */
export function describeRequestError(error: unknown): RequestErrorCopy {
  if (!(error instanceof ApiError)) {
    const hasMessage = error instanceof Error && Boolean(error.message);
    return {
      titleKey: 'guest.errors.genericTitle',
      detailKey: hasMessage ? 'guest.errors.serverMessage' : 'guest.errors.genericDetail',
      detailParams: hasMessage ? { message: (error as Error).message } : undefined,
      tone: 'danger',
    };
  }

  const code = error.code ?? '';

  // MVP-11 #33 (P1, guest vs. cockpit disagreement): the server's 409 already carries the
  // PRE-EXISTING request, run through the exact same `mapForGuest()` every other read path uses
  // (song-request controller's `create()`, `ctx.body.data = ...mapForGuest(priorForSong)`) — so
  // `error.data.status` here is the request's true, reconciled state (pending / accepted / and,
  // for an accepted row whose queue-entry already finished or was pulled, 'played'/'rejected' —
  // see `mapForGuest`'s own doc comment, song-request service `:127-177`). Reusing
  // `describeQueuePosition` (the SAME copy `QueuePositionCard` renders for that status) means the
  // guest sees exactly which state blocked them — "Already on the list — waiting for the KJ.",
  // "Already accepted — #3 in line", "You already sang this one tonight." — instead of one
  // generic "Already on the list" that can flatly contradict what their own request card, or the
  // cockpit tray, already shows for the same row.
  //
  // MVP-272 Phase 1: `describeQueuePosition` (`guestRefetch.ts`) now returns a key+params like this
  // module does, rather than a rendered English sentence — its key/params are threaded straight
  // through as this branch's own `detailKey`/`detailParams` (no `guest.errors.serverMessage`
  // passthrough needed; that key is reserved for genuinely dynamic, server-authored text).
  //
  // This paragraph used to end by saying the block ITSELF was still wrong — that the duplicate
  // check read the row's raw `status` column, which stays 'accepted' forever, so a song that had
  // already played still blocked every re-request that night — and that fixing it was "flagged for
  // a follow-up" server-side. That follow-up landed: MVP-210 made `isDuplicateRequest` ask the
  // queue entry, and MVP-521 then made it and `mapForGuest()` share ONE derivation
  // (`deriveRequestState` in `request-guard.js`) so the two ends cannot drift apart again. A
  // request whose entry is done derives to 'played' and one that was skipped/removed to
  // 'rejected'; both are terminal and both are re-askable.
  //
  // The stale sentence outlived its defect by a year and cost a later ticket (MVP-521) its whole
  // premise — it was filed as a live bug on the strength of this comment, and the red-first test
  // came up green. Kept as a correction rather than deleted, because the branch below is still the
  // reason this module exists: the 409 carries the PRE-EXISTING request and the guest is shown its
  // reconciled state, not a generic refusal.
  if (code === 'DUPLICATE_REQUEST' && error instanceof GuestApiError && error.data) {
    const data = error.data;
    const titleKey: MessageKey =
      data.status === 'pending'
        ? 'guest.errors.duplicateRequest'
        : data.status === 'accepted'
          ? data.nowPlaying
            ? 'guest.errors.duplicateNowPlaying'
            : 'guest.errors.duplicateAccepted'
          : data.status === 'played'
            ? 'guest.errors.duplicatePlayed'
            : 'guest.errors.duplicateAsked';
    const queueCopy = describeQueuePosition(data, data.etaSec);
    return {
      titleKey,
      detailKey: queueCopy.key,
      detailParams: queueCopy.params,
      tone: 'info',
    };
  }

  // MVP-473: before the `error.message` check below, because for these two codes that message is a
  // machine string. The host rides in `details` (`networkError()` puts it there); with no host to
  // name, the sentence would render a literal `{host}` — `interpolate()` deliberately leaves an
  // unfilled slot visible — so that case falls back to the copy that names no machine at all.
  const networkDetailKey = NETWORK_DETAIL_KEY[code];
  if (networkDetailKey) {
    const host = typeof error.details?.host === 'string' ? error.details.host : null;
    return {
      titleKey: 'guest.errors.networkError',
      detailKey: host ? networkDetailKey : 'guest.errors.genericDetail',
      detailParams: host ? { host } : undefined,
      tone: 'danger',
    };
  }

  const tone = TONE_BY_CODE[code] ?? 'danger';
  const detailKey: MessageKey =
    code === 'SESSION_NOT_FOUND'
      ? SESSION_NOT_FOUND_DETAIL_KEY
      : error.message
        ? 'guest.errors.serverMessage'
        : 'guest.errors.tryAgainDetail';
  const detailParams = detailKey === 'guest.errors.serverMessage' ? { message: error.message } : undefined;
  const retryAfterSec = retryAfterOf(error);
  // P1 fix (round 3). MVP-625: this paragraph used to end by declaring the real bug server-side —
  // an open-mode auto-accepted request leaking guest-device's `pendingCount`, with song-request's
  // controller yet to release the reservation in that branch. It releases it, and has since:
  // `strapi-template/src/api/song-request/controllers/song-request.js:280`, `if
  // (session.requestMode === 'open') await release();`, right after the row is created, with its
  // own paragraph above it explaining why. That branch is closed; do not go looking in it.
  //
  // A `pendingCount` problem DOES still exist, for an unrelated reason: the counter is keyed on
  // `deviceIdHash` alone and is never scoped to a session, so a pending row stranded by a night
  // ending keeps holding that device's slot into the next one. That is MVP-621 — chase it there,
  // not in the open-mode branch. (Eight lines up, at the DUPLICATE_REQUEST block, is this file's
  // own record of what a stale sentence like the old one costs: it outlived its defect by a year
  // and cost MVP-521 its whole premise.)
  //
  // The swap below is unaffected either way. "Slow down" implies a timer worth waiting out, which is exactly
  // backwards for the one RATE_LIMITED reason that has none (request-guard.js's `reason:
  // 'pending'` always sends `retryAfterSec: null`, unlike 'window'/the coarse global limit, which
  // always send a number) — swapping the TITLE only (never `detail`, still the server's own
  // sentence) keeps this file's one rule intact.
  const titleKey: MessageKey =
    code === 'RATE_LIMITED' && retryAfterSec === undefined
      ? 'guest.errors.alreadyInQueue'
      : (TITLE_KEY_BY_CODE[code] ?? 'guest.errors.genericTitle');

  return retryAfterSec === undefined
    ? { titleKey, detailKey, detailParams, tone }
    : { titleKey, detailKey, detailParams, tone, retryAfterSec };
}
